import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    ReactNode
} from 'react'

import Link from 'next/link'
import { ArrowRight, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'light' | 'outline-light'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE = 'group/button relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition-[background-color,color,border-color,box-shadow] duration-300 ease-out disabled:pointer-events-none disabled:opacity-60'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-burgundy text-cream shadow-[inset_0_1px_0_rgb(255_255_255/0.08),0_12px_28px_-14px_rgb(57_26_24/0.7)] hover:bg-burgundy-soft',
    secondary: 'border border-burgundy/20 text-burgundy hover:border-burgundy/40 hover:bg-burgundy/[0.04]',
    ghost: 'text-burgundy hover:bg-burgundy/[0.06]',
    light: 'bg-cream text-burgundy shadow-[0_12px_28px_-14px_rgb(0_0_0/0.5)] hover:bg-ivory',
    'outline-light': 'border border-cream/25 text-cream hover:border-cream/50 hover:bg-cream/[0.06]'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-[13px]',
    md: 'h-11 px-5 text-sm',
    lg: 'h-[52px] px-7 text-[15px]'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => {
    return cn(BASE, VARIANTS[variant], SIZES[size], className)
}

const Arrow: FC = (): ReactNode => {
    return (
        <ArrowRight
            aria-hidden='true'
            className='size-4 transition-transform duration-300 ease-out group-hover/button:translate-x-0.5'
        />
    )
}

interface ButtonLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    variant?: ButtonVariant
    size?: ButtonSize
    arrow?: boolean
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    variant,
    size,
    arrow = false,
    className,
    children,
    ...rest
}): ReactNode => {
    const classes = buttonClasses(variant, size, className)

    if (/^(https?:|mailto:|tel:)/.test(href)) {
        return (
            <a href={href} className={classes} {...rest}>
                {children}
                {arrow && <Arrow />}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} {...rest}>
            {children}
            {arrow && <Arrow />}
        </Link>
    )
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    arrow?: boolean
    loading?: boolean
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    arrow = false,
    loading = false,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
}): ReactNode => {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={buttonClasses(variant, size, className)}
            {...rest}
        >
            {loading && <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />}
            {children}
            {arrow && !loading && <Arrow />}
        </button>
    )
}
